import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useGroupMenuEngineering — scatter data for the cross-unit menu
// engineering overlay for ONE group. The group analogue of
// useMenuEngineering.
//
// FIX HIGH-1 (RLS asymmetry): this hook used to read
// vw_menu_engineering_group CLIENT-SIDE. That view is security_invoker,
// so its base tables (items / item_sales) are RLS-scoped to
// restaurant_members.user_id = auth.uid(). A group OWNER who is NOT a
// member of a unit would see that unit's dots DISAPPEAR from the scatter
// — while the service-role AI read named the same unit with dollar
// figures. We now source the scatter from the get-group-menu-engineering-
// scatter edge fn (service-role read gated by resolveGroupOwner), exactly
// like useGroupKpi → get-group-kpi-snapshot. The owner now sees the
// IDENTICAL unit set the AI read reasons over. Same body shape, same
// raw-fetch pattern as the KPI hook.
//
// One row per (unit, menu item with a recipe). Each row carries the base
// vw_menu_engineering columns plus group_id + restaurant_name +
// restaurant_id. The edge fn returns the FULL row set (NO <5-units noise
// filter — that lives only in the AI read) plus the derived distinct
// units, so the return SHAPE is unchanged for GroupMenuEngineering.jsx.
//
// Returns:
//   rows        — every classified item across the group's units
//   units       — distinct { restaurant_id, restaurant_name }, name-sorted
//   hasData     — true if at least one row exists
//   loading, error, refresh
//
// DECOUPLED from the AI strategic read (useGroupMenuEngineeringRead): the
// scatter renders from these rows whether or not the AI read has loaded.
// No cache here — a plain read each time, same as useGroupKpi.
export function useGroupMenuEngineering(groupId) {
  const [rows, setRows]       = useState([])
  const [units, setUnits]     = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  // Synchronous in-flight guard — set before the first await, cleared in
  // finally. Stops two quick mounts/refreshes from double-firing.
  const inFlightRef = useRef(false)

  const fetchScatter = useCallback(async () => {
    // Null/undefined groupId → do NOT fire (no bad request). The edge fn
    // would 403 on a missing group_id anyway; short-circuit cleanly.
    if (!groupId) {
      setRows([])
      setUnits([])
      setLoading(false)
      setError(null)
      return
    }
    if (inFlightRef.current) return

    inFlightRef.current = true
    setLoading(true)
    setError(null)
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/get-group-menu-engineering-scatter`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        // Body is just { group_id }; the server's group-ownership gate is
        // the tenant boundary.
        body: JSON.stringify({ group_id: groupId }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const payload = await resp.json()
      if (payload?.error) {
        throw new Error(payload.message || 'Could not load group menu engineering data.')
      }
      setRows(Array.isArray(payload?.rows) ? payload.rows : [])
      setUnits(Array.isArray(payload?.units) ? payload.units : [])
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useGroupMenuEngineering] scatter fetch failed', e)
      setError('Could not load group menu engineering data.')
      setRows([])
      setUnits([])
    } finally {
      inFlightRef.current = false
      setLoading(false)
    }
  }, [groupId])

  // Initial load — kick off once groupId resolves.
  useEffect(() => {
    fetchScatter()
  }, [fetchScatter])

  // The edge fn already returns units name-sorted + deduped. Fall back to
  // deriving from rows defensively (e.g. an older fn response shape).
  const derivedUnits = useMemo(() => {
    if (units.length > 0) return units
    const byId = new Map()
    for (const r of rows) {
      if (r.restaurant_id && !byId.has(r.restaurant_id)) {
        byId.set(r.restaurant_id, {
          restaurant_id: r.restaurant_id,
          restaurant_name: r.restaurant_name || 'Unit',
        })
      }
    }
    return [...byId.values()].sort((a, b) =>
      (a.restaurant_name || '').localeCompare(b.restaurant_name || ''),
    )
  }, [units, rows])

  return {
    rows,
    units: derivedUnits,
    hasData: rows.length > 0,
    loading,
    error,
    refresh: () => fetchScatter(),
  }
}
